import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { LinearProgress, Button, Alert, Typography } from "@mui/material";
import ExperimentControls from "../ExperimentControls";
import { FetchLoadingStateEnum } from "../../constants";
import {
  fetchAnalysisLoadingState,
  selectActiveJurisdiction,
  selectAnalysisErrorMessage,
  selectJurisdictions
} from "../../selectors/suggestions";
import { selectIsRunning } from "../../selectors/experiments";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import { DocumentSettings } from "../../models/documentSettings";
import JurisdictionDropDown from '../JurisdictionDropDown';
import useAnalysisPageStyles from './analysis-page.styles';
import { Jurisdiction, setActiveJurisdiction } from '../../redux/suggestions';

export interface AnalysisPageProps {
  startAIAnalysis: (options: { jurisdictionId?: string }) => void;
  documentSettings: DocumentSettings;
}

const AnalysisPage: React.FC<AnalysisPageProps> = ({ startAIAnalysis, documentSettings }) => {
  const dispatch = useDispatch();
  const selectedJurisdictionId = useSelector(selectActiveJurisdiction);
  const jurisdictions = useSelector(selectJurisdictions);
  const selectedJurisdiction = jurisdictions.find(j => j.id === selectedJurisdictionId) || null;
  const analysisErrorMessage = useSelector(selectAnalysisErrorMessage);
  const isExperimentRunning = useSelector(selectIsRunning);
  const loading: FetchLoadingStateEnum = useSelector(fetchAnalysisLoadingState);
  const { getFlag } = useFeatureFlags();
  const showUiTestButton = getFlag('showUiTestButton');
  const projectJurisdictionSelectorCP1 = getFlag('projectJurisdictionSelectorCp1');
  const documentSettingsIsLoading = documentSettings.isLoading === true;
  const isLoading = loading === FetchLoadingStateEnum.loading || documentSettingsIsLoading;

  const handleJurisdictionChange = (_: React.SyntheticEvent, value: Jurisdiction) => {
    dispatch(setActiveJurisdiction(value ? value.id : null));
  };

  const classes = useAnalysisPageStyles();
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.topHeader}>
          <Typography variant="h1">ExactReport</Typography>
          <Typography variant="h2">AI Review</Typography>
        </div>
        <Typography className={classes.description}>
          The best transfer pricing assistance at your side. Ensuring jurisdiction-specific compliance in minutes, not days.
        </Typography>
      </div>

      {projectJurisdictionSelectorCP1 &&
        <div className={classes.selectContainer}>
          <Typography variant="caption">Select the jurisdiction for your document to enable analysis aligned precisely with its specific regulations.</Typography>
          <JurisdictionDropDown
            disabled={isLoading}
            value={selectedJurisdiction}
            onChange={handleJurisdictionChange}
            label="Jurisdiction"
          />
        </div>
      }

      {loading === FetchLoadingStateEnum.error && (
        <Alert severity="error" style={{ margin: '2rem 0', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
          {analysisErrorMessage}
        </Alert>
      )}

      {showUiTestButton && <ExperimentControls />}

      <Button
        fullWidth
        className={classes.button}
        color="primary"
        variant="contained"
        onClick={() => startAIAnalysis({})}
        disabled={(projectJurisdictionSelectorCP1 && !selectedJurisdiction) || isLoading}
      >
        Start Analysis
      </Button>

      {isLoading && !isExperimentRunning &&
        <div className={classes.linearProgress}>
          <LinearProgress aria-label="Loading AI suggestions" />
        </div>
      }
    </div>
  );
};

export default AnalysisPage;
